import { createClient } from '@supabase/supabase-js';

/**
 * The one Supabase client for the whole app.
 *
 * This uses the publishable (anon) key, which is public by design — it is
 * compiled into the browser bundle. All access control is RLS in the database.
 * If a page renders blank, suspect a missing policy before suspecting the
 * query: RLS denial returns zero rows, not an error (CLAUDE.md trap #3).
 */
const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !key) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY. ' +
      'Copy .env.example to .env.local and fill it in.',
  );
}

/**
 * Which database are we actually talking to?
 *
 * `npm run dev` points at the local stack; `npm run dev:hosted` points at the
 * real project. `App.tsx` shows a LOCAL banner when this is true (the HOSTED
 * banner was removed 2026-09-10).
 */
export const isLocalStack = /127\.0\.0\.1|localhost/.test(url);

export const supabase = createClient(url, key, {
  auth: {
    // Managers sign in with an emailed one-time code (M3). No passwords, no
    // PINs, and identity is never Google.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

/**
 * Parse a YYYY-MM-DD key as a local date at midnight.
 *
 * `new Date('2026-07-28')` parses as UTC midnight, which in Eastern time is
 * 8pm on the 27th. This does not. Do not reach for toISOString() either — it
 * converts to UTC first, so any evening in Eastern time yields tomorrow's
 * date. This is CLAUDE.md trap #1 and it has already bitten this project
 * twice. Dates cross the wire as YYYY-MM-DD strings and are parsed as local
 * dates at the RPC boundary.
 */
export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) throw new Error(`Not a YYYY-MM-DD date key: ${key}`);
  return new Date(y, m - 1, d);
}
